import { AxiosError } from 'axios';
import { find } from 'lodash';
import React, { useCallback, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { bankAliasesApi } from 'api';
import {
  CloseFormikDialogData,
  CrudPage,
  CrudTableActionType,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { BankAlias, FilterDefinition } from 'types';
import { validationUtils } from 'utils';

import { BankAliasDetailsDialog } from './BankAliasDetailsDialog';

type BankAliasFilters = {
  id: string;
  alias: string;
  bankId: string;
};

export const BankAliasesList: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.bank_aliases',
  });

  const { banks } = useUserContext();

  const queryClient = useQueryClient();

  const queryResult = usePartialQuery(
    QueryKey.BankAliases,
    bankAliasesApi.getAllPaginated,
  );

  const { mutate: remove } = useMutation(bankAliasesApi.remove, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.BankAliases);
    },
    notifierType: 'remove',
  });

  const { mutate: update } = useMutation<
    BankAlias,
    AxiosError,
    { id: string; data: Partial<BankAlias> }
  >(bankAliasesApi.update);

  const columns: DataGridColumnDefinition<BankAlias>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.alias'),
        valueKey: 'alias',
      },
      {
        header: t('fields.bank'),
        valueKey: 'bankId',
        valueFormatter: (bankId) => find(banks, { id: bankId })?.name,
      },
    ],
    [t, banks],
  );

  const handleUpdate = useCallback(
    (
      item: BankAlias,
      data: CloseFormikDialogData<Partial<BankAlias>>,
      closeDialog: () => void,
    ) => {
      update(
        { id: item.id, data: data?.values },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.BankAliases);
            closeDialog();
          },
          onError: (error: AxiosError) => {
            data?.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        },
      );
    },
    [queryClient, update],
  );

  const filtersDefinitions: FilterDefinition<BankAliasFilters>[] = useMemo(
    () => [
      {
        label: t('fields.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('fields.alias'),
        name: 'alias',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('fields.bank'),
        name: 'bankId',
        type: FilterDefinitionType.Bank,
      },
    ],
    [t],
  );

  return (
    <Fragment>
      <CrudPage
        filters={{ filtersDefinitions }}
        table={{
          queryResult,
          columns,
          paginated: true,
          actions: [
            {
              type: CrudTableActionType.Details,
              renderDialog: BankAliasDetailsDialog,
              onUpdate: handleUpdate,
            },
            {
              type: CrudTableActionType.Remove,
              onRemove: (item: BankAlias, { close }) =>
                remove(item.id, { onSuccess: close }),
            },
          ],
        }}
      />
    </Fragment>
  );
};
